import { NavLink, useLocation } from 'react-router-dom';
import {
  Bell,
  Store,
  Settings as SettingsIcon,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  Moon,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn, Tooltip } from '@/components/ui';
import { KanameMark } from '@/components/brand/KanameMark';
import { KanameWordmark } from '@/components/brand/KanameWordmark';
import { useGust } from '@/components/brand/useGust';
import { ProfileAvatarMenu } from './ProfileAvatarMenu';
import { useTheme } from '@/theme/useTheme';

/*
  PrimaryRail (Sprint 1.14): фиксы по visual checkpoint 2026-05-21.

  - X и nav icons НА ОДНОЙ ВЕРТИКАЛЬНОЙ ЛИНИИ при collapse — все элементы
    имеют одинаковую horizontal padding (px-3 ВСЕГДА). Никаких toggle'ов
    `justify-center` / `px-2` ↔ `p-3` которые делали X скачок на 4-80px
    при collapse.
  - Indicator измеряется через ResizeObserver — re-measures на КАЖДЫЙ
    layout-change aside (включая mid-transition фреймы). Это исправляет
    bug когда indicator улетал за границу rail (старый useLayoutEffect
    мерил один раз на момент commit, до того как rail успел сжаться).
  - Rail width transition 400ms → 500ms ease-in-out (медленнее, smooth).
*/

interface RailItem {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: 'dot' | 'urgent' | { count: number };
  onClick?: () => void;
  participateInIndicator?: boolean;
}

interface PrimaryRailProps {
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenAISpotlight: () => void;
  onToggleNotifications: () => void;
  notificationsCount?: number;
}

interface IndicatorBox {
  top: number;
  left: number;
  width: number;
  height: number;
  visible: boolean;
}

export function PrimaryRail({
  expanded,
  onToggleExpand,
  onOpenAISpotlight,
  onToggleNotifications,
  notificationsCount = 0,
}: PrimaryRailProps) {
  const asideRef = useRef<HTMLElement>(null);
  const [indicator, setIndicator] = useState<IndicatorBox>({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    visible: false,
  });
  // Initial render — пропускаем CSS transition чтобы indicator не "вылетал"
  // из (0,0,0,0) на нужное место. После первого measure включаем transitions.
  const [hasInitialized, setHasInitialized] = useState(false);

  const location = useLocation();

  // Re-measure indicator on layout changes. ResizeObserver fires ON EVERY
  // frame during rail width transition → indicator tracks active item
  // smoothly, без race conditions.
  //
  // Sprint 1.16: добавлен `expanded` в deps — при collapse/expand смещается
  // data-coral-active с NavLink (w-full) на icon-wrap (48x48) и обратно
  // (см. RailItemView). Effect пере-меряет новый target и CSS плавно
  // морфит размер indicator'а.
  useLayoutEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;

    const measure = () => {
      const activeEl = aside.querySelector<HTMLElement>('[data-coral-active="true"]');
      if (activeEl) {
        const asideRect = aside.getBoundingClientRect();
        const itemRect = activeEl.getBoundingClientRect();
        setIndicator({
          top: itemRect.top - asideRect.top,
          left: itemRect.left - asideRect.left,
          width: itemRect.width,
          height: itemRect.height,
          visible: true,
        });
      } else {
        setIndicator((prev) => ({ ...prev, visible: false }));
      }
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(aside);
    // Также наблюдаем за каждым потенциальным active элементом — на случай
    // если они изменяются по размеру независимо.
    aside.querySelectorAll('[data-coral-active="true"]').forEach((el) => ro.observe(el));

    return () => ro.disconnect();
  }, [location.pathname, expanded]);

  // Enable transitions on indicator после первого measure (пропускаем initial)
  useEffect(() => {
    if (indicator.visible && !hasInitialized) {
      // Wait one frame чтобы initial position применился без transition
      const t = window.setTimeout(() => setHasInitialized(true), 50);
      return () => window.clearTimeout(t);
    }
  }, [indicator.visible, hasInitialized]);

  const topItems: RailItem[] = [
    { to: '/marketplace', label: 'Marketplace', icon: Store, participateInIndicator: true },
  ];

  const bottomItems: RailItem[] = [
    {
      to: '#notifications',
      label: 'Уведомления',
      icon: Bell,
      badge: notificationsCount > 0 ? { count: notificationsCount } : undefined,
      onClick: onToggleNotifications,
    },
    { to: '/settings', label: 'Настройки', icon: SettingsIcon, participateInIndicator: true },
  ];

  return (
    // Sprint 1.16: wrapper убран — FloatingToggle переехал ВНУТРЬ aside
    // (bottom-right corner, не половинит граничку). Aside напрямую flex-
    // child AppShell с items-stretch → растягивается по высоте.
    <aside
      ref={asideRef}
      data-expanded={expanded || undefined}
      className={cn(
        'relative flex shrink-0 flex-col overflow-hidden rounded-lg',
        'text-rail-fg',
        // 500ms ease-in-out — медленнее, плавнее (Sprint 1.14)
        'transition-rail-width duration-slow ease-in-out',
        expanded ? 'w-60' : 'w-20',
      )}
      style={{
        background:
          'linear-gradient(180deg, ' +
          'color-mix(in srgb, var(--rail) 92%, white 8%) 0%, ' +
          'var(--rail) 40%, ' +
          'color-mix(in srgb, var(--rail) 95%, black 5%) 100%)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.08), ' +
          'inset -1px 0 0 rgba(255,255,255,0.04), ' +
          '0 10px 30px -10px rgba(0,0,0,0.35)',
      }}
      aria-label="Главная навигация"
    >
      {/* Sliding indicator — Sprint 1.15: width использует ease-out (не bouncy),
          чтобы не overshoot'ить за rail boundary когда target обновляется
          ResizeObserver'ом во время rail collapse. Top/left/height
          остаются bouncy — это «прыжок в гнездо» при vertical-морфе. */}
      <div
        aria-hidden
        className="pointer-events-none absolute z-base rounded-md bg-gradient-to-br from-brand-highlight via-brand-accent to-brand-action-pressed"
        style={{
          top: `${indicator.top}px`,
          left: `${indicator.left}px`,
          width: `${indicator.width}px`,
          height: `${indicator.height}px`,
          opacity: indicator.visible ? 1 : 0,
          boxShadow:
            '0 6px 18px -4px rgba(255,110,92,0.55), ' +
            'inset 0 1px 0 rgba(255,255,255,0.25), ' +
            'inset 0 -1px 0 rgba(0,0,0,0.1)',
          transition: hasInitialized
            ? 'top 380ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'left 320ms cubic-bezier(0.4, 0, 0.2, 1), ' +
              'width 320ms cubic-bezier(0.4, 0, 0.2, 1), ' +
              'height 380ms cubic-bezier(0.34, 1.56, 0.64, 1), ' +
              'opacity 200ms ease-out'
            : 'opacity 200ms ease-out', // initial: no morph transition, just fade-in
        }}
      />

      {/* Brand header */}
      <BrandHeader expanded={expanded} />

      {/* Top nav — px-4 ВСЕГДА (Sprint 1.17: px-3 → px-4 для symmetric icons
          в collapsed mode. С px-3 правое поле было 20px вместо 12px из-за
          NavLink w-full=56 и icon-wrap 48 prilipшего к левому краю.
          px-4 даёт inner=48 = icon size → symmetric 16/16). */}
      <nav className="relative z-raised flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-1">
        {topItems.map((item) => (
          <RailItemView key={item.to} item={item} expanded={expanded} />
        ))}

        <RailDivider expanded={expanded} />

        <RailButton
          icon={<Sparkles size={20} />}
          label="AI"
          tooltip="AI Spotlight · ⌘J"
          expanded={expanded}
          onClick={onOpenAISpotlight}
          accent="ai"
        />
        {bottomItems.map((item) => (
          <RailItemView key={item.to} item={item} expanded={expanded} />
        ))}
      </nav>

      {/* Footer — Sprint 1.17:
          - p-3 → px-4 py-3 (symmetric icons aligned с nav)
          - Toggle переехал из `absolute` в третью flex-row footer'а (под
            avatar). Решает conflict с avatar.chevron из 1.16. */}
      <div className="relative z-raised flex shrink-0 flex-col gap-2 border-t border-rail-divider px-4 py-3">
        <ThemeToggleButton expanded={expanded} />
        <ProfileAvatarMenu expanded={expanded} />
        {/* justify-end ВСЕГДА (не conditional) — toggle stays в right side
            и в expanded и в collapsed. Conditional justify вызывал X-jump
            в Sprint 1.13 — не повторяем. */}
        <div className="flex justify-end pt-1">
          <RailCollapseToggle expanded={expanded} onClick={onToggleExpand} />
        </div>
      </div>
    </aside>
  );
}

// ----- Brand header — X с фиксированным positioning -----

function BrandHeader({ expanded }: { expanded: boolean }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  // Пасхалка: три быстрых нажатия на знак — порыв ветра, веер раскручивается.
  const { gust, tap } = useGust();

  return (
    // Sprint 1.17: p-3 → px-4 py-3. Horizontal padding 16 даёт inner=48 в
    // collapsed → icon-wrap 48x48 fits exactly → symmetric icons.
    // Vertical paddding 12 (как было) — header height = 12+48+12 = 72.
    <div className="relative z-raised flex shrink-0 items-center px-4 py-3">
      <NavLink
        to="/"
        aria-label="На главную"
        onClick={tap}
        className="group/brand flex h-12 w-full shrink-0 items-center outline-none"
      >
        {/* Знак Kaname вместо буквы K (docs/brand-concept.md). Коралловое выделение на главную не
            переезжает: дома живёт ядро знака и мягкое свечение вокруг. */}
        <span className="relative grid size-12 shrink-0 place-items-center">
          <span
            aria-hidden
            className={cn(
              'absolute inset-0 rounded-full transition-opacity duration-smooth brand-halo',
              isHome ? 'opacity-100' : 'opacity-0',
            )}
          />
          <KanameMark
            breathing={isHome}
            life="both"
            gust={gust}
            className="relative size-10 transition-transform duration-normal group-hover/brand:scale-105"
          />
        </span>
        <span
          className={cn(
            'overflow-hidden whitespace-nowrap',
            'transition-rail-label duration-slow ease-in-out',
            expanded ? 'ml-3 max-w-rail-label opacity-100 delay-100' : 'ml-0 max-w-0 opacity-0',
          )}
        >
          <KanameWordmark />
        </span>
      </NavLink>
    </div>
  );
}

function RailDivider({ expanded }: { expanded: boolean }) {
  return (
    <span
      aria-hidden
      className={cn('my-2 block h-px shrink-0 bg-rail-separator', expanded ? 'w-full' : 'mx-auto w-8')}
    />
  );
}

function ThemeToggleButton({ expanded }: { expanded: boolean }) {
  const { mode, toggle } = useTheme();
  const isDark = mode === 'dark';
  const Icon = isDark ? Sun : Moon;
  const label = isDark ? 'Светлая тема' : 'Тёмная тема';
  const button = (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      // НЕТ conditional padding — рендерим item как nav-items (px-0, icon-wrap 48x48)
      className="flex h-12 w-full shrink-0 items-center rounded-md text-rail-fg-muted transition-all duration-normal outline-none hover:bg-rail-hover hover:text-rail-fg"
    >
      <span className="grid size-12 shrink-0 place-items-center">
        <Icon size={18} strokeWidth={2} className="transition-transform duration-smooth hover:rotate-12" />
      </span>
      <span
        className={cn(
          'overflow-hidden text-left text-sm font-medium whitespace-nowrap',
          'transition-rail-text duration-slow ease-in-out',
          expanded ? 'max-w-rail-label flex-1 opacity-100 delay-100' : 'max-w-0 opacity-0',
        )}
      >
        {label}
      </span>
    </button>
  );
  return expanded ? (
    button
  ) : (
    <Tooltip content={label} side="right">
      {button}
    </Tooltip>
  );
}

function RailItemView({ item, expanded }: { item: RailItem; expanded: boolean }) {
  const Icon = item.icon;
  const location = useLocation();
  const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
  const isCoralActive: boolean = isActive && !!item.participateInIndicator;

  const className = cn(
    'group/item relative z-raised flex h-12 w-full shrink-0 items-center rounded-md outline-none',
    'transition-surface-text duration-normal ease-out',
    isCoralActive
      ? 'text-white' // coral дает indicator снизу (full-width на NavLink)
      : isActive
        ? 'bg-rail-active text-rail-fg'
        : 'text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg',
  );

  // Sprint 1.16: data-coral-active живёт на РАЗНЫХ элементах в зависимости
  // от expanded state:
  //   - expanded=true   → на NavLink (w-full) → indicator растягивается под
  //                       icon+label, выглядит как «active card»
  //   - expanded=false  → на icon-wrap (48x48) → indicator идеально hugs
  //                       иконку, без 8px swing'а вправо от w-full=56
  // useLayoutEffect deps включает expanded → при collapse/expand новый
  // target меряется → CSS smoothly морфит размер.
  const navLinkCoral = isCoralActive && expanded;
  const iconCoral = isCoralActive && !expanded;

  const inner = (
    <>
      <span
        data-coral-active={iconCoral ? 'true' : undefined}
        className="grid size-12 shrink-0 place-items-center"
      >
        <Icon size={20} strokeWidth={isCoralActive ? 2.2 : 2} />
      </span>
      <span
        className={cn(
          'overflow-hidden text-left text-sm font-medium whitespace-nowrap',
          'transition-rail-label duration-slow ease-in-out',
          expanded ? 'ml-1 max-w-rail-detail flex-1 opacity-100 delay-100' : 'max-w-0 opacity-0',
        )}
      >
        {item.label}
      </span>
      {item.badge && expanded && <BadgeView badge={item.badge} expanded={expanded} active={isCoralActive} />}
      {item.badge && !expanded && <BadgeView badge={item.badge} expanded={expanded} active={isCoralActive} />}
    </>
  );

  const dataAttrs = navLinkCoral ? { 'data-coral-active': 'true' } : {};

  if (item.onClick) {
    const button = (
      <button
        type="button"
        onClick={item.onClick}
        className={className}
        aria-label={item.label}
        {...dataAttrs}
      >
        {inner}
      </button>
    );
    return expanded ? (
      button
    ) : (
      <Tooltip content={item.label} side="right">
        {button}
      </Tooltip>
    );
  }
  const link = (
    <NavLink to={item.to} className={className} aria-label={item.label} {...dataAttrs}>
      {inner}
    </NavLink>
  );
  return expanded ? (
    link
  ) : (
    <Tooltip content={item.label} side="right">
      {link}
    </Tooltip>
  );
}

interface RailButtonProps {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  expanded: boolean;
  onClick: () => void;
  accent?: 'ai';
}

function RailButton({ icon, label, tooltip, expanded, onClick, accent }: RailButtonProps) {
  const isAi = accent === 'ai';
  const button = (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'group/btn relative z-raised flex h-12 w-full shrink-0 items-center rounded-md transition-all duration-normal outline-none',
        isAi
          ? 'text-ai-rail-text hover:bg-ai-accent/10 hover:text-ai-hover-text'
          : 'text-rail-fg-muted hover:bg-rail-hover hover:text-rail-fg',
      )}
    >
      <span className="grid size-12 shrink-0 place-items-center">{icon}</span>
      <span
        className={cn(
          'overflow-hidden text-left text-sm font-medium whitespace-nowrap',
          'transition-rail-text duration-slow ease-in-out',
          expanded ? 'ml-1 max-w-rail-detail flex-1 opacity-100 delay-100' : 'max-w-0 opacity-0',
        )}
      >
        {label}
      </span>
    </button>
  );
  return expanded ? (
    button
  ) : (
    <Tooltip content={tooltip ?? label} side="right">
      {button}
    </Tooltip>
  );
}

// ----- Collapse toggle (Sprint 1.17 redesign) -----

/*
  RailCollapseToggle — круглый полупрозрачный handle в footer'е (3-я строка
  под ProfileAvatarMenu).

  Эволюция:
  - Sprint 1.15: «ушко» half-stick за right edge через translate-x-1/2.
    Юзер хотел дискретнее.
  - Sprint 1.16: circular `absolute bottom-3 right-2` внутри aside.
    Overlapping'ал avatar.chevron в expanded mode.
  - Sprint 1.17: убран `absolute`, стал нормальным flex-child третьей
    строки footer'а. Никаких overlap'ов. Right-aligned через
    `<div className="flex justify-end">` обёртку в PrimaryRail.

  Стили:
  - Circle 28x28 (h-7 w-7) — меньше чем avatar (48), не доминирует
  - opacity 0.4 idle → 1.0 hover/focus
  - Иконки PanelLeftClose ↔ PanelLeftOpen с cross-fade + rotate-12
*/
function RailCollapseToggle({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <Tooltip content={expanded ? 'Свернуть' : 'Развернуть'} side="right">
      <button
        type="button"
        onClick={onClick}
        aria-label={expanded ? 'Свернуть rail' : 'Развернуть rail'}
        data-state={expanded ? 'expanded' : 'collapsed'}
        className={cn(
          'group/floating grid size-7 shrink-0 place-items-center',
          'rounded-full outline-none',
          // Полупрозрачный backdrop, чуть видим в idle
          'bg-rail-control backdrop-blur-sm',
          'border border-rail-control-line',
          'text-rail-fg-muted',
          'opacity-40',
          'transition-rail-grip',
          // Hover: вытаскиваем в полный фокус
          'hover:scale-110 hover:opacity-100',
          'hover:border-rail-control-hover-line hover:bg-rail-control-hover',
          'hover:text-rail-fg',
          'hover:shadow-glow-brand',
          'focus-visible:opacity-100 focus-visible:shadow-rail-focus',
        )}
      >
        <span className="relative size-3">
          <PanelLeftClose
            size={12}
            className={cn(
              'absolute inset-0',
              'transition-rail-grip-icon',
              expanded ? 'rotate-0 opacity-100' : 'scale-90 -rotate-12 opacity-0',
            )}
          />
          <PanelLeftOpen
            size={12}
            className={cn(
              'absolute inset-0',
              'transition-rail-grip-icon',
              expanded ? 'scale-90 rotate-12 opacity-0' : 'rotate-0 opacity-100',
            )}
          />
        </span>
      </button>
    </Tooltip>
  );
}

function BadgeView({
  badge,
  expanded,
  active,
}: {
  badge: NonNullable<RailItem['badge']>;
  expanded: boolean;
  active: boolean;
}) {
  if (badge === 'dot' || badge === 'urgent') {
    return (
      <span
        className={cn(
          'absolute top-2 right-2 size-2 rounded-full border-2 border-rail',
          badge === 'urgent' ? 'bg-notice-error-accent' : 'bg-brand-accent',
        )}
      />
    );
  }
  if (typeof badge === 'object' && 'count' in badge) {
    const label = badge.count > 99 ? '99+' : String(badge.count);
    if (expanded) {
      return (
        <span
          className={cn(
            'mr-3 rounded-xs px-1.5 py-0.5 text-2xs leading-none font-semibold',
            active ? 'bg-white/25 text-white' : 'bg-brand-accent text-white',
          )}
        >
          {label}
        </span>
      );
    }
    return (
      <span className="absolute top-2 right-2 size-2 rounded-full border-2 border-rail bg-brand-accent" />
    );
  }
  return null;
}
